"use client";

import Link from "next/link";
import { useState } from "react";
import { plainText } from "@/lib/assistant-format";
import { assistantSessionId } from "@/lib/assistant-session";
import LinkifiedText from "@/app/components/LinkifiedText";

interface Match {
  name: string;
  slug: string;
  why: string;
}

/**
 * Подбор компаний под задачу: пользователь описывает, что нужно, ассистент
 * выбирает 3–5 компаний из каталога и объясняет, почему они подходят.
 */
export default function CompanyMatcher({ locale }: { locale: string }) {
  const isEn = locale.startsWith("en");
  const [task, setTask] = useState("");
  const [reply, setReply] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ask = async () => {
    const text = task.trim();
    if (!text || loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/assistant", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scene: "company-match",
          slug: "catalog",
          message: text,
          locale,
          sessionId: assistantSessionId(),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(typeof data?.error === "string" ? data.error : "Не удалось подобрать");
        return;
      }
      setReply(typeof data?.reply === "string" ? plainText(data.reply) : "");
      setMatches(Array.isArray(data?.matches) ? data.matches : []);
    } catch {
      setError(isEn ? "Could not reach the assistant" : "Не удалось подобрать");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
      <h2 className="text-base font-semibold text-white">
        {isEn ? "Not sure who to pick?" : "Не знаете, кого выбрать?"}
      </h2>
      <p className="mt-1 text-sm text-white/60">
        {isEn
          ? "Describe the task — the assistant will pick companies from the catalog and explain why."
          : "Опишите задачу — ассистент подберёт компании из каталога и объяснит, почему они подходят."}
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          value={task}
          onChange={(event) => setTask(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void ask();
          }}
          placeholder={
            isEn
              ? "For example: mobile app for food delivery, launch in 4 months"
              : "Например: мобильное приложение для доставки еды, запуск через 4 месяца"
          }
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => void ask()}
          disabled={loading || !task.trim()}
          className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#202e64] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (isEn ? "Matching…" : "Подбираю…") : isEn ? "Find companies" : "Подобрать"}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-red-300">{error}</p>}

      {(reply || matches.length > 0) && (
        <div className="mt-5 space-y-4">
          {reply && <p className="text-sm leading-relaxed text-white/75"><LinkifiedText text={reply} /></p>}
          {matches.length > 0 && (
            <div className="grid gap-3 md:grid-cols-2">
              {matches.map((match) => (
                <Link
                  key={match.slug}
                  href={`/${locale}/companies/${match.slug}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/20 hover:bg-white/[0.06]"
                >
                  <div className="text-sm font-semibold text-white">{match.name}</div>
                  {match.why && <p className="mt-1 text-xs leading-relaxed text-white/60">{match.why}</p>}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
